import { color } from "@rneui/base";
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Platform, SectionList, StyleSheet, View } from "react-native";
import { Button, Input, useTheme } from "react-native-elements";
import { Gesture, GestureDetector } from "react-native-gesture-handler";
import { clearGlobalFlag, setGlobalFlag } from "../backend/Flags";
import { PlayerContext } from "../backend/PlayerContext";
import {
  allocateApprenticesToSpell,
  calculateApprenticeTotal,
  canAutocast,
  clearLoadoutPosition,
  getApprenticeAllocation,
  getAutocastSchoolLevelRequirements,
  getEstimatedApprenticeManaConsumptionPerSec,
  getFreeApprentices,
  getFreeApprenticesCached,
  getSmarterApprenticesTurnedOn,
  getSmarterApprenticesUnlocked,
  saveLoadoutToPosition,
} from "../backend/autocast/Autocast";
import { applyTransformationsCached } from "../backend/calculation/Calculation";
import { TransformationTags } from "../backend/calculation/TransformationTags";
import { School, getSchoolLevel } from "../backend/spells/ElementsAndSchools";
import { Spell, SpellActionBase } from "../backend/spells/Spell";
import { getVisibleSpellsBySchool } from "../backend/spells/Spells";
import { calculateIncomePerSecond } from "../backend/timetick/IncomeOverTime";
import { formatNumber } from "../utils/FormattingUtils";
import ActionsContainer from "./utility/ActionsContainer";
import { AutocastLoadoutLoadContainer } from "./utility/AutocastLoadoutLoadContainer";
import CategoryContainer, { CategoryHeader } from "./utility/CategoryContainer";
import { CheckBox } from "./utility/CheckBox";
import { useGestureHandler } from "./utility/Miscellaneous";
import { Picker as UtilityPicker } from "./utility/Picker";
import { PlusMinusAllocator } from "./utility/PlusMinusAllocator";
import { Text } from "./utility/Text";
import { UIContext, useUIStateFromContext } from "./utility/UIContext";

function SpellRow(props: { spell: Spell; applyAll: boolean; isOdd: boolean }) {
  const spell = props.spell;
  const playerContext = useContext(PlayerContext);
  const playerContextRef = useRef(playerContext);
  playerContextRef.current = playerContext;
  const spellName = spell.getSpellName();
  const apprenticeAllocation = getApprenticeAllocation(playerContext);
  const current = apprenticeAllocation?.[spellName] || 0;
  const secs = applyTransformationsCached(
    [TransformationTags.AutocastDelay],
    playerContext,
    60.0,
  );
  const smarterApprentices = getSmarterApprenticesTurnedOn(playerContext);
  const cooldown =
    current > 0 ? (spell as SpellActionBase).getCooldown(playerContext) : 0;
  const delay = useMemo(() => {
    const suggestedDelay = spell.getEstimatedMinAutocastDelay(playerContext);
    const delay =
      current > 0
        ? smarterApprentices
          ? Math.max(secs / current, suggestedDelay, cooldown)
          : Math.max(secs / current, cooldown)
        : 0;
    return delay;
  }, [
    current > 0 && spell.getEstimatedMinAutocastDelay(playerContext),
    current > 0 && smarterApprentices,
    current > 0 && secs,
    current > 0 && current,
    current > 0 && cooldown,
  ]);
  const currentExtra = useMemo(
    () =>
      current > 0
        ? `(${formatNumber(delay, {
            showDecimals: true,
          })}s/cast)`
        : "",
    [current, delay],
  );
  const canAllocate = canAutocast(playerContext, spell);
  const freeApprentices = getFreeApprenticesCached(playerContext);
  const hasFreeApprentices = freeApprentices > 0;
  const onPressMinus = useCallback(
    () =>
      playerContextRef.current.apply(
        allocateApprenticesToSpell(spell, props.applyAll ? -Infinity : -1),
      ),
    [spell, props.applyAll],
  );
  const onPressPlus = useCallback(
    () =>
      playerContextRef.current.apply(
        allocateApprenticesToSpell(spell, props.applyAll ? Infinity : 1),
      ),
    [spell, props.applyAll],
  );
  const plusMinusContainer = useMemo(
    () => (
      <View style={styles.plusMinusAllocatorContainer}>
        <PlusMinusAllocator
          disableMinus={current === 0}
          disablePlus={!hasFreeApprentices}
          onPressMinus={onPressMinus}
          onPressPlus={onPressPlus}
        />
      </View>
    ),
    [current, hasFreeApprentices, onPressMinus, onPressPlus],
  );
  const allocatorExtras = useMemo(
    () => (
      <>
        <View style={styles.frequencyTextContainer}>
          <Text>{currentExtra}</Text>
        </View>
        <Text>{current}</Text>
      </>
    ),
    [currentExtra, current],
  );
  const allocator = useMemo(() => {
    let allocatorTemp = (
      <View style={styles.row}>
        {allocatorExtras}
        {plusMinusContainer}
      </View>
    );
    if (!canAllocate) {
      const schoolReqs = getAutocastSchoolLevelRequirements(spell);
      let allReqs = [];
      for (let school in schoolReqs) {
        if (
          getSchoolLevel(playerContext, school as School) <
          schoolReqs[school as School]
        ) {
          allReqs.push(`${school} Lv${schoolReqs[school as School]}`);
        }
      }
      allocatorTemp = (
        <Text style={styles.allocatorText}>Requires: {allReqs.join(", ")}</Text>
      );
    }
    return allocatorTemp;
  }, [allocatorExtras, canAllocate, spell, plusMinusContainer]);

  const nameContainer = useMemo(
    () => (
      <View style={styles.spellNameContainer}>
        <Text numberOfLines={3}>{spellName}:</Text>
      </View>
    ),
    [spellName],
  );

  const theme = useTheme();

  return useMemo(
    () => (
      <View
        style={[
          styles.row,
          styles.spellRowContainer,
          props.isOdd && {
            backgroundColor: color(theme.theme.colors.grey5).alpha(0.5),
          },
        ]}
      >
        {nameContainer}
        {allocator}
      </View>
    ),
    [spellName, allocator, props.isOdd, theme],
  );
}

// function SpellRowMemoized(props: { spell: Spell }) {
//   const playerContext = useContext(PlayerContext);
//   const uiContext = useContext(UIContext);

//   return useMemo(
//     () => (
//       <SpellRow
//         playerContext={playerContext}
//         spell={props.spell}
//       />
//     ),
//     [props.spell.getName(), visible === true ? playerContext : false],
//   );
// }

const VIEWABILITY_CONFIG = {
  minimumViewTime: 0,
  itemVisiblePercentThreshold: 1,
  waitForInteraction: false,
};

function emptySpaceHeader() {
  return <View style={styles.emptySpaceHeader} />;
}

export function Autocast(props: {
  onSwipeLeft: () => void;
  onSwipeRight: () => void;
}) {
  const playerContext = useContext(PlayerContext);
  const playerContextRef = useRef(playerContext);
  playerContextRef.current = playerContext;

  const loadoutAmount = 8;
  const [slotNumber, setSlotNumber] = useState(
    playerContext?.autocast?.lastLoadoutUsed?.toString() ?? "0",
  );

  const spellsBySchool = getVisibleSpellsBySchool(playerContext);
  const freeApprentices = getFreeApprentices(playerContext);

  const toggleApprenticesPaused = useCallback(() => {
    playerContextRef.current.apply((state) => {
      state.autocast.apprenticesPaused = !state.autocast.apprenticesPaused;
      return state;
    });
  }, []);

  const loadoutsSlotPicker = useMemo(() => {
    const items = [];
    for (let i = 0; i < loadoutAmount; i++) {
      items.push({
        key: i.toString(),
        value: i.toString(),
        label: `Slot ${i + 1}`,
      });
    }
    return (
      <UtilityPicker
        onValueChange={setSlotNumber}
        selectedValue={slotNumber}
        data={items}
      />
    );
  }, [loadoutAmount, setSlotNumber, slotNumber]);

  const totalCost = getEstimatedApprenticeManaConsumptionPerSec(playerContext);
  const manaIncome = calculateIncomePerSecond(playerContext)?.Mana ?? 0;
  const totalApprentices = calculateApprenticeTotal(playerContext);
  const theme = useTheme();

  const uiContext = useContext(UIContext);

  const [applyAll, setApplyAll] = useUIStateFromContext(
    uiContext,
    "autocast_applyall",
    false,
  );

  const toggleApplyAll = useCallback(() => {
    setApplyAll(!applyAll);
  }, [applyAll, setApplyAll]);

  const apprenticeData = useMemo(
    () => (
      <View
        style={[
          styles.apprenticeDataContainer,
          { borderTopColor: theme.theme.colors.divider },
        ]}
      >
        <View style={styles.assignAllContainer}>
          <CheckBox
            title="Assign / unassign all at once"
            checked={applyAll}
            textStyle={styles.checkboxText}
            containerStyle={styles.checkboxContainer}
            onPress={toggleApplyAll}
          />
        </View>
        <View style={[styles.row, styles.totalApprenticesContainer]}>
          <Text>Apprentices Available: </Text>
          <Text>
            {formatNumber(freeApprentices)}/{formatNumber(totalApprentices)}
          </Text>
        </View>
        <View style={[styles.row, styles.manaCostContainer]}>
          <Text>Estimated Mana consumption: </Text>
          <Text>{formatNumber(totalCost, { showDecimals: true })}/sec</Text>
        </View>
        <View style={[styles.apprenticeWarningContainer]}>
          {totalCost > manaIncome && (
            <Text style={{ color: theme.theme.colors.warning }}>
              Warning: Your Mana income may not be enough to sustain the
              Apprentices!
            </Text>
          )}
        </View>
      </View>
    ),
    [
      totalCost,
      manaIncome,
      freeApprentices,
      totalApprentices,
      toggleApplyAll,
      applyAll,
      theme,
    ],
  );

  const loadoutObjects = playerContext.global.autocastLoadouts;

  const [loadoutName, setLoadoutName] = useState("");

  useEffect(
    () =>
      setLoadoutName(
        slotNumber != null
          ? loadoutObjects?.[Number.parseInt(slotNumber)]?.name ?? ""
          : "",
      ),
    [slotNumber],
  );

  const setSlotNumberString = useCallback(
    (position: number) => setSlotNumber(position.toString()),
    [setSlotNumber],
  );

  useEffect(() => {
    setSlotNumber(playerContext.autocast.lastLoadoutUsed.toString());
  }, [playerContext.autocast.lastLoadoutUsed]);

  const unlockedSmarterApprentices =
    getSmarterApprenticesUnlocked(playerContext);
  const smarterApprentices = getSmarterApprenticesTurnedOn(playerContext);

  const toggleSmarterApprentices = useCallback(() => {
    playerContextRef.current.apply((state) => {
      if (getSmarterApprenticesTurnedOn(state)) {
        return clearGlobalFlag("smarter_apprentices_turned_on")(state);
      } else {
        return setGlobalFlag("smarter_apprentices_turned_on")(state);
      }
    });
  }, []);

  const footer = useMemo(
    () => (
      <View>
        <View style={[styles.marginBottom, styles.marginTop]}>
          {playerContext.autocast.apprenticesPaused ? (
            <Button
              title="Unpause Apprentices"
              onPress={toggleApprenticesPaused}
            />
          ) : (
            <Button
              title="Pause Apprentices"
              onPress={toggleApprenticesPaused}
            />
          )}
        </View>
        {unlockedSmarterApprentices && (
          <View style={styles.marginBottom}>
            <CheckBox
              title="Enable smarter apprentices"
              checked={smarterApprentices}
              textStyle={styles.checkboxText}
              containerStyle={styles.checkboxContainer}
              onPress={toggleSmarterApprentices}
            />
          </View>
        )}
        <CategoryContainer title="Loadouts">
          <View style={[styles.loadoutsRow, styles.marginBottom]}>
            <Text>Save:</Text>
            <View style={styles.loadoutRest}>
              <View style={styles.pickerContainer}>{loadoutsSlotPicker}</View>
              <Input
                placeholder="Name..."
                value={loadoutName}
                onChangeText={setLoadoutName}
                containerStyle={styles.loadoutNameContainer}
                inputContainerStyle={styles.loadoutNameInput}
                inputStyle={styles.loadoutNameActualInput}
                maxLength={20}
                renderErrorMessage={false}
              />
            </View>
          </View>
          <View style={[styles.loadoutsActions, styles.marginBottom]}>
            <View style={styles.loadoutAction}>
              <Button
                title="Save"
                onPress={() => {
                  playerContextRef.current.apply((state) =>
                    saveLoadoutToPosition(
                      state,
                      Number.parseInt(slotNumber),
                      loadoutName,
                    ),
                  );
                }}
              />
            </View>
            <View style={styles.loadoutAction}>
              <Button
                title="Clear"
                onPress={() => {
                  setLoadoutName("");
                  playerContextRef.current.apply((state) =>
                    clearLoadoutPosition(state, Number.parseInt(slotNumber)),
                  );
                }}
              />
            </View>
          </View>
          <AutocastLoadoutLoadContainer />
        </CategoryContainer>
        <ActionsContainer area="Autocast" />
      </View>
    ),
    [
      slotNumber,
      loadoutsSlotPicker,
      loadoutName,
      setLoadoutName,
      playerContext.autocast.apprenticesPaused,
      toggleApprenticesPaused,
      freeApprentices,
      totalApprentices,
      totalCost,
      manaIncome,
      applyAll,
      toggleApplyAll,
      setSlotNumberString,
      smarterApprentices,
      toggleSmarterApprentices,
      unlockedSmarterApprentices,
    ],
  );

  const spellsBySchoolJSON = JSON.stringify(
    Object.values(spellsBySchool).map((spellList) =>
      spellList.map((spell) => spell.getName()),
    ),
  );
  const listData = useMemo(
    () =>
      Object.entries(spellsBySchool).map((item) => ({
        title: item[0],
        data: item[1],
      })),
    [spellsBySchoolJSON],
  );
  const renderItem = useCallback(
    (item: { item: Spell; index: number }) => (
      <SpellRow
        key={item.item.getName()}
        isOdd={item.index % 2 != 0}
        spell={item.item}
        applyAll={applyAll}
      />
    ),
    [applyAll],
  );
  const keyExtractor = useCallback((item: Spell) => item?.getName?.(), []);
  const categoryContainerStyle = useMemo(
    () => ({ backgroundColor: theme.theme.colors.background }),
    [theme],
  );
  const renderSectionHeader = useCallback(
    (info: { section: { title: string; data: Spell[] } }) => (
      <CategoryHeader
        title={info.section.title}
        containerStyle={categoryContainerStyle}
      />
    ),
    [categoryContainerStyle],
  );
  const sectionList = useMemo(
    () => (
      <SectionList
        sections={listData}
        initialNumToRender={30}
        renderItem={renderItem}
        keyExtractor={keyExtractor}
        renderSectionHeader={renderSectionHeader}
        ListFooterComponent={footer}
        style={styles.sectionListStyle}
        contentContainerStyle={styles.sectionListInnerStyle}
      />
    ),
    [listData, renderItem, keyExtractor, renderSectionHeader, footer],
  );

  const swipeHandlerGesture = useGestureHandler(
    props.onSwipeLeft,
    props.onSwipeRight,
  );
  const nativeGesture = useMemo(() => Gesture.Native(), []);
  const compositeGesture = useMemo(
    () => Gesture.Simultaneous(swipeHandlerGesture, nativeGesture),
    [swipeHandlerGesture, nativeGesture],
  );

  const swipeHandlerGestureBottom = useGestureHandler(
    props.onSwipeLeft,
    props.onSwipeRight,
  );
  const nativeGestureBottom = useMemo(() => Gesture.Native(), []);
  const compositeGestureBottom = useMemo(
    () => Gesture.Simultaneous(swipeHandlerGestureBottom, nativeGestureBottom),
    [swipeHandlerGestureBottom, nativeGestureBottom],
  );

  const contents = useMemo(
    () => (
      <View style={styles.fullContainer}>
        <GestureDetector key="autocastGesture" gesture={compositeGesture}>
          {sectionList}
        </GestureDetector>
        <GestureDetector key="dataGesture" gesture={compositeGestureBottom}>
          {apprenticeData}
        </GestureDetector>
      </View>
    ),
    [sectionList, apprenticeData, compositeGesture, compositeGestureBottom],
  );

  return contents;
}

const styles = StyleSheet.create({
  fullContainer: {
    flex: 1,
    paddingHorizontal: 8,
  },
  sectionListStyle: {
    flex: 1,
  },
  sectionListInnerStyle: {
    paddingBottom: 16,
  },
  marginBottom: {
    marginBottom: 20,
  },
  marginTop: {
    marginTop: 40,
  },
  row: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  plusMinusAllocatorContainer: {
    marginLeft: 16,
  },
  totalApprenticesContainer: {
    marginBottom: 8,
    paddingRight: 8,
  },
  manaCostContainer: {
    marginBottom: 4,
    paddingRight: 8,
  },
  spellRowContainer: {
    padding: 4,
    minHeight: 32,
  },
  allocatorText: {
    marginLeft: 0,
    flex: 1,
    textAlign: "right",
  },
  spellNameContainer: {
    flex: 1,
    marginRight: 8,
  },
  loadoutsRow: {
    marginTop: 16,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  loadoutsActions: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "flex-end",
  },
  loadoutAction: {
    marginLeft: 8,
  },
  loadoutRest: { flexDirection: "row", alignItems: "center" },
  topPickerContainer: {
    marginTop: 8,
  },
  emptySpaceHeader: {
    height: 40,
  },
  pickerContainer: {
    width: 80,
    marginLeft: 8,
    ...Platform.select({
      android: { backgroundColor: "#eee", borderRadius: 10, default: {} },
    }),
  },
  frequencyTextContainer: {
    marginRight: 8,
  },
  apprenticeWarningContainer: {
    height: 40,
    marginBottom: 8,
  },
  spellContainersContainer: {
    marginTop: 16,
  },
  emptyCategoryTitle: {
    opacity: 0,
  },
  betweenSpellsDivider: {
    opacity: 0.4,
  },
  checkboxContainer: {
    marginLeft: 0,
    marginTop: 8,
    padding: 0,
  },
  checkboxText: {
    fontWeight: "normal",
  },
  assignAllContainer: {
    marginBottom: 8,
  },
  apprenticeDataContainer: {
    borderTopWidth: 1,
  },
  loadoutNameContainer: {
    width: 148,
    alignItems: "center",
    justifyContent: "center",
    marginLeft: 8,
    flexDirection: "row",
    paddingHorizontal: 0,
  },
  loadoutNameInput: {
    width: 148,
    paddingHorizontal: 4,
    borderWidth: 1,
  },
  loadoutNameActualInput: {
    width: 140,
  },
});
